import AppComponent from '../base/app-component.ts';

/**
 * wc-gestione-passkey — Gestione passkey nella pagina profilo.
 *
 * Attributi:
 *   data-options  JSON: { credentials: [...], currentRpId: "..." }
 *
 * Mostra lista passkey registrate, consente registrarne di nuove
 * e rimuovere quelle esistenti. Usa Weext dialogs (no alert/confirm/prompt nativi).
 */
export class GestionePasskey extends AppComponent {
  static override componentName = 'wc-gestione-passkey';

  protected override get templateId() { return 'gestione-passkey'; }

  protected override afterRender(): void {
    // Controlla supporto WebAuthn
    if (!window.PublicKeyCredential) {
      const unsupported = this.$('[data-passkey-unsupported]');
      unsupported?.classList.remove('d-none');

      const registerBtn = this.$('[data-action="register-passkey"]') as HTMLButtonElement | null;
      if (registerBtn) registerBtn.disabled = true;
      return;
    }

    // Delegate click su azioni
    this.delegate.on<MouseEvent>('click', '[data-action="register-passkey"]', () => {
      this.handleRegister();
    });

    this.delegate.on<MouseEvent>('click', '[data-action="remove-passkey"]', (e) => {
      const btn = (e.target as HTMLElement);
      const credId = btn.dataset.credentialId;
      const deviceName = btn.dataset.deviceName;
      if (credId) this.handleRemove(credId, deviceName || 'Passkey');
    });
  }

  // --- Registrazione passkey ---

  private async handleRegister(): Promise<void> {
    const btn = this.$('[data-action="register-passkey"]') as HTMLButtonElement | null;
    if (!btn) return;

    // Chiedi nome dispositivo tramite Weext.prompt
    let deviceName = await Weext.prompt('Nome dispositivo (opzionale):', {
      title: 'Registra passkey',
      placeholder: 'es. MacBook Pro',
    });

    // null = annullato
    if (deviceName === null) return;

    // Vuoto = usa user agent abbreviato
    if (!deviceName) {
      deviceName = navigator.userAgent.split(' ').pop() || 'Passkey';
    }

    // Disabilita bottone con spinner
    btn.disabled = true;
    const originalHtml = btn.innerHTML;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Attendere...';

    try {
      // 1. Richiedi opzioni al server
      const startResp = await fetch('/auth/passkey/register/start', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
      });

      if (!startResp.ok) {
        const startErr = await startResp.json().catch(() => ({})) as { error?: string };
        throw new Error(startErr.error || 'Errore server (HTTP ' + startResp.status + ')');
      }

      const data = await startResp.json() as { options: Record<string, unknown>; sessionId: string };

      // 2. Prompt biometrico browser
      const attestation = await SimpleWebAuthnBrowser.startRegistration({ optionsJSON: data.options });

      // 3. Invia al server per verifica
      const verifyResp = await fetch('/auth/passkey/register/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attestation, sessionId: data.sessionId, deviceName }),
      });

      if (verifyResp.ok) {
        Weext.toast('Passkey registrata', { type: 'success' });
        location.reload();
      } else {
        const err = await verifyResp.json().catch(() => ({})) as { error?: string };
        throw new Error(err.error || 'Errore nella verifica della passkey');
      }
    } catch (err: unknown) {
      // NotAllowedError: utente ha cancellato il prompt, ignora
      if (err instanceof Error && err.name === 'NotAllowedError') return;

      console.error('Errore registrazione passkey:', err);
      const message = err instanceof Error ? err.message : 'Errore nella registrazione della passkey';
      await Weext.alert(message, {
        title: 'Errore',
        icon: 'bi-x-circle-fill',
        iconClass: 'text-danger',
      });
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalHtml;
    }
  }

  // --- Rimozione passkey ---

  private async handleRemove(credentialId: string, deviceName: string): Promise<void> {
    const ok = await Weext.confirm('Rimuovere la passkey "' + deviceName + '"?', {
      title: 'Conferma rimozione',
      confirmText: 'Rimuovi',
      confirmClass: 'btn-danger',
      icon: 'bi-trash3-fill',
      iconClass: 'text-danger',
    });

    if (!ok) return;

    try {
      const resp = await fetch('/auth/passkey/' + credentialId, { method: 'DELETE' });

      if (resp.ok) {
        // Rimuovi la riga dal DOM
        const btn = this.$(`[data-action="remove-passkey"][data-credential-id="${credentialId}"]`);
        const row = btn?.closest('tr');
        row?.remove();

        Weext.toast('Passkey rimossa', { type: 'success' });

        // Se nessuna riga rimasta, ricarica per mostrare stato vuoto
        if (!this.$('[data-passkey-list] tr')) {
          location.reload();
        }
      } else {
        const err = await resp.json().catch(() => ({})) as { error?: string };
        await Weext.alert(err.error || 'Errore nella rimozione della passkey', {
          title: 'Errore',
          icon: 'bi-x-circle-fill',
          iconClass: 'text-danger',
        });
      }
    } catch (err: unknown) {
      console.error('Errore rimozione passkey:', err);
      await Weext.alert('Errore nella rimozione della passkey', {
        title: 'Errore',
        icon: 'bi-x-circle-fill',
        iconClass: 'text-danger',
      });
    }
  }
}
